import { NavigateNextRounded } from "@mui/icons-material";
import { Link, Typography } from "@mui/material";
import { useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { ROUTES, resolveSidebarLabel } from "@/constants";
import { useTranslation } from "@/context/I18nProvider";
import { useAdminSidebars } from "@/hooks";
import { StyledBreadcrumbs } from "@/theme";
import { AdminSidebar } from "@/types";

// Walk from the row matching the current path up through its `parent` chain to
// build the full ancestor trail (root → leaf). Returns [] for unknown routes,
// so the breadcrumb falls back to just the Dashboard anchor instead of going
// blank. Resolving via `parent` handles arbitrarily nested groups (e.g. the
// 3-level Footer pages) without a fixed depth.
const buildTrail = (rows: AdminSidebar[], pathname: string): AdminSidebar[] => {
  const byId = new Map(rows.map((row) => [String(row._id), row]));
  const trail: AdminSidebar[] = [];

  let node = rows.find((row) => row.url === pathname);
  while (node) {
    trail.unshift(node);
    node = node.parent ? byId.get(String(node.parent)) : undefined;
  }

  return trail;
};

export default function NavbarBreadcrumbs() {
  const { t } = useTranslation();
  const location = useLocation();
  const rows = useAdminSidebars();

  // Dashboard is rendered as the constant home anchor below, so drop it from
  // the trail to avoid showing the crumb twice on the home page.
  const trail = useMemo(
    () => buildTrail(rows, location.pathname).filter((row) => row.name !== "dashboard"),
    [rows, location.pathname],
  );
  const atHome = location.pathname === ROUTES.HOME;

  return (
    <StyledBreadcrumbs aria-label="breadcrumb" separator={<NavigateNextRounded fontSize="small" />}>
      {atHome ? (
        <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 600 }}>
          {t("nav.dashboard")}
        </Typography>
      ) : (
        <Link
          component={RouterLink}
          to={ROUTES.HOME}
          variant="body1"
          color="inherit"
          underline="hover"
        >
          {t("nav.dashboard")}
        </Link>
      )}
      {trail.map((row, index) => {
        const label = resolveSidebarLabel(row.name, row.label, t);
        const isLast = index === trail.length - 1;
        return (
          <Typography
            key={String(row._id)}
            variant="body1"
            aria-current={isLast ? "page" : undefined}
            sx={isLast ? { color: "text.primary", fontWeight: 600 } : undefined}
          >
            {label}
          </Typography>
        );
      })}
    </StyledBreadcrumbs>
  );
}
